type HashtagOption = { value: string; label: string };

/**
 * Catalog hashtags from `products.product.hashtags` (TempoVIP inventory).
 * Values are the exact strings stored on products — keyword matching is
 * case-insensitive. Labels are display-friendly (same casing as catalog).
 *
 * Ordered by approximate frequency on sold/visible products (most common first).
 * Only these tags can be selected in the builder — no free-text interests.
 */
export const PRODUCT_HASHTAGS: readonly HashtagOption[] = [
  { value: 'Nature', label: 'Nature' },
  { value: 'Art&Photography', label: 'Art & photography' },
  { value: 'Beach', label: 'Beach' },
  { value: 'Cultural', label: 'Cultural' },
  { value: 'Wellness', label: 'Wellness' },
  { value: 'Foodie', label: 'Foodie' },
  { value: 'Luxury', label: 'Luxury' },
  { value: 'Boutique', label: 'Boutique' },
  { value: 'Sustainable', label: 'Sustainable' },
  { value: 'Seclusion', label: 'Seclusion' },
  { value: 'Historic', label: 'Historic' },
  { value: 'Ecotourism', label: 'Ecotourism' },
  { value: 'Urban', label: 'Urban' },
  { value: 'Vintage', label: 'Vintage' },
  { value: 'Modern', label: 'Modern' },
  { value: 'Masterclass', label: 'Masterclass' },
  { value: 'WineExperience', label: 'Wine experience' },
  { value: 'Adventure', label: 'Adventure' },
  { value: 'Sport', label: 'Sport' },
  { value: 'MeetTheOwner', label: 'Meet the owner' },
  { value: 'Landmark', label: 'Landmark' },
  { value: 'Boat', label: 'Boat' },
  { value: 'Family', label: 'Family' },
  { value: 'Romantic', label: 'Romantic' },
  { value: 'Music', label: 'Music' },
  { value: 'Golf', label: 'Golf' },
  { value: 'Relais&Châteaux', label: 'Relais & Châteaux' },
  { value: 'CookingClass', label: 'Cooking class' },
  { value: 'SmallLuxuryHotels', label: 'Small Luxury Hotels' },
  { value: 'Virtuoso', label: 'Virtuoso' },
  { value: 'Serandipians', label: 'Serandipians' },
  { value: 'ArabHeritage', label: 'Arab heritage' },
  { value: 'Religious', label: 'Religious' },
  { value: 'Kids', label: 'Kids' },
  { value: 'UrbanTrails', label: 'Urban trails' },
  { value: 'Bike', label: 'Bike' },
  { value: 'JewishHeritage', label: 'Jewish heritage' },
] as const;

const HASHTAG_VALUE_SET = new Set(
  PRODUCT_HASHTAGS.map((o) => o.value.toLowerCase())
);

/**
 * Options for the builder hashtag multi-select.
 * Only catalog tags — selected values not in the list are dropped from options
 * (and should be filtered from form state on load).
 */
export const hashtagOptionsForValues = (
  selected: readonly string[]
): HashtagOption[] => {
  // Prefer canonical casing from PRODUCT_HASHTAGS; ignore unknown free-text.
  void selected;
  return [...PRODUCT_HASHTAGS];
};

/** Keep only known catalog hashtags (migrate away from free-text interests). */
export const filterKnownHashtags = (values: readonly string[]): string[] => {
  const byLower = new Map(
    PRODUCT_HASHTAGS.map((o) => [o.value.toLowerCase(), o.value] as const)
  );
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const key = raw.trim().toLowerCase();
    if (!key || !HASHTAG_VALUE_SET.has(key)) continue;
    const canonical = byLower.get(key);
    if (!canonical || seen.has(canonical.toLowerCase())) continue;
    seen.add(canonical.toLowerCase());
    out.push(canonical);
  }
  return out;
};

/**
 * Map legacy free-text interests (old form / golden briefs) → catalog hashtags.
 */
export const LEGACY_INTEREST_TO_HASHTAG: Readonly<Record<string, string>> = {
  food: 'Foodie',
  wine: 'WineExperience',
  beach: 'Beach',
  culture: 'Cultural',
  city: 'Urban',
  golf: 'Golf',
  sightseeing: 'Cultural',
  museum: 'Cultural',
  nature: 'Nature',
  family: 'Family',
  palace: 'Landmark',
  anniversary: 'Romantic',
  adventure: 'Adventure',
  spa: 'Wellness',
  hiking: 'Nature',
  art: 'Art&Photography',
  history: 'Historic',
  nightlife: 'Urban',
  shopping: 'Boutique',
  yacht: 'Boat',
};

/** Normalize keywords array to catalog hashtag values only. */
export const normalizeKeywordsToHashtags = (
  values: readonly string[]
): string[] => {
  const mapped = values.map((v) => {
    const t = v.trim();
    if (!t) return '';
    const legacy = LEGACY_INTEREST_TO_HASHTAG[t.toLowerCase()];
    return legacy ?? t;
  });
  return filterKnownHashtags(mapped);
};
